import React from "react";
import {
  Info,
  MapPin,
  Upload,
  Trash2,
  Image as ImageIcon,
  Save,
} from "lucide-react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./HomebuyerPropertyInformation.css";
import { useAuth0 } from "@auth0/auth0-react";
import { updateDossierPropertyInfo } from "./homebuyerDossierApi";
import { PROPERTY_PURCHASE_COUNTRIES } from "../../../../data/countries";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const GOOGLE_MAPS_MAP_ID =
  process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || "DEMO_MAP_ID";

const DEFAULT_FORM = {
  property_id: null,
  prefilled_from_existing_property: false,

  property_type: "",
  building_condition: "",
  surface_sqm: "",
  number_of_rooms: "",
  year_of_build: "",
  has_lift: "",
  comfort_type: "",
  notes: "",

  street_name: "",
  house_number: "",
  city: "",
  postal_code: "",
  country: "",
  apartment_number: "",
  full_address: "",

  latitude: null,
  longitude: null,
  place_id: "",

  epc_rating_current: "",
  epc_rating_target: "",
  kwh_sqm_year_current: "",
  kwh_sqm_year_target: "",
  heating_type: "",
  cooling_type: "",

  land_register_id: "",
  ownership_type: "",

  photos: [],
};

const REQUIRED_PROPERTY_FIELDS = [
  "property_type",
  "building_condition",
  "surface_sqm",
  "year_of_build",
  "epc_rating_current",
  "kwh_sqm_year_current",
  "kwh_sqm_year_target",
  "heating_type",
  "cooling_type",
  "ownership_type",
  "street_name",
  "house_number",
  "city",
  "market_region",
  "full_address",
  "postal_code",
  "country",
];

const OPTION_VALUES = {
  property_type: [
    "APARTMENT_FLAT",
    "STUDIO_APARTMENT",
    "SINGLE_FAMILY_HOUSE_DETACHED",
    "SEMI_DETACHED_HOUSE",
    "TERRACED_ROW_HOUSE",
    "DUPLEX_MAISONETTE",
    "TOWNHOUSE",
    "MULTI_FAMILY_RESIDENTIAL_BUILDING",
    "OTHER",
  ],
  building_condition: [
    "EXCELLENT",
    "VERY_GOOD",
    "GOOD",
    "FAIR_AVERAGE",
    "POOR",
    "DILAPIDATED",
    "UNKNOWN",
    "OTHER",
  ],
  heating_type: [
    "NONE",
    "GAS_BOILER",
    "AIR_CONDITION",
    "HEAT_PUMP",
    "DISTRICT_HEATING",
    "UNKNOWN",
    "OTHER",
  ],
  cooling_type: ["NONE", "AIR_CONDITION", "UNKNOWN", "OTHER"],
  ownership_type: [
    "SOLE_OWNER",
    "CO_OWNER_SPOUSE_PARTNER",
    "CO_OWNER_OTHER_PERSONS",
    "USUFRUCT_RIGHT_OF_USE",
    "OTHER",
  ],
};

function hydrateOtherFields(savedProperty) {
  const formPatch = {};
  const otherPatch = {};

  Object.entries(OPTION_VALUES).forEach(([field, allowedValues]) => {
    const value = savedProperty?.[field];

    if (value && !allowedValues.includes(value)) {
      formPatch[field] = "OTHER";
      otherPatch[field] = value;
    }
  });

  return { formPatch, otherPatch };
}

function isRequiredField(fieldName, country, isFlandersMarket = false) {
  if (fieldName === "land_register_id") {
    return normalizeCountryCode(country) === "ES";
  }

  // The Flemish EPC target is required; Girona's is optional.
  if (fieldName === "epc_rating_target") {
    return isFlandersMarket;
  }

  return REQUIRED_PROPERTY_FIELDS.includes(fieldName);
}

function isFilled(value) {
  return value !== undefined && value !== null && String(value).trim() !== "";
}

function loadGoogleMaps(apiKey) {
  if (!apiKey) {
    return Promise.resolve(null);
  }

  if (window.google?.maps?.importLibrary) {
    return Promise.resolve(window.google);
  }

  if (window.__legupGoogleMapsPromise) {
    return window.__legupGoogleMapsPromise;
  }

  window.__legupGoogleMapsPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById("legup-google-maps-script");

    if (existing) {
      if (window.google?.maps?.importLibrary) {
        resolve(window.google);
        return;
      }

      existing.addEventListener("error", reject, { once: true });

      return;
    }

    const callbackName = "__legupGoogleMapsLoaded";

    window[callbackName] = () => {
      try {
        resolve(window.google);
      } finally {
        delete window[callbackName];
      }
    };

    const script = document.createElement("script");

    script.id = "legup-google-maps-script";
    script.async = true;

    const params = new URLSearchParams({
      key: apiKey,
      loading: "async",
      callback: callbackName,
      v: "weekly",
    });

    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;

    script.onerror = (error) => {
      delete window[callbackName];
      window.__legupGoogleMapsPromise = null;
      reject(error);
    };

    document.head.appendChild(script);
  });

  return window.__legupGoogleMapsPromise;
}

function getPlaceAddressPart(place, type, useShortText = false) {
  const component = place?.addressComponents?.find((item) =>
    item.types?.includes(type),
  );

  if (!component) {
    return "";
  }

  return useShortText ? component.shortText || "" : component.longText || "";
}

function buildFullAddress(form) {
  return [
    [form.street_name, form.house_number].filter(Boolean).join(" "),
    form.apartment_number ? `Apt ${form.apartment_number}` : "",
    [form.postal_code, form.city].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
}

function mapExistingPropertyToForm(existingProperty) {
  if (!existingProperty) return {};

  return {
    property_id: existingProperty.id || null,
    prefilled_from_existing_property: true,

    property_type: existingProperty.property_type || "",
    building_condition: existingProperty.building_condition || "",
    surface_sqm: existingProperty.surface_sqm || "",
    number_of_rooms: existingProperty.number_of_rooms || "",
    year_of_build: existingProperty.year_of_build || "",

    has_lift:
      existingProperty.has_lift === true
        ? "YES"
        : existingProperty.has_lift === false
          ? "NO"
          : "",

    comfort_type: existingProperty.comfort_type || "",
    notes: existingProperty.notes || "",

    street_name: existingProperty.street_name || "",
    house_number: existingProperty.house_number || "",
    city: existingProperty.city || "",
    postal_code: existingProperty.postal_code || "",
    country: existingProperty.country || "",
    apartment_number: existingProperty.apartment_number || "",
    full_address: existingProperty.full_address || "",

    latitude: existingProperty.latitude || null,
    longitude: existingProperty.longitude || null,
    place_id: existingProperty.place_id || "",

    epc_rating_current: existingProperty.epc_rating_current || "",
    epc_rating_target: existingProperty.epc_rating_target || "",
    kwh_sqm_year_current: existingProperty.kwh_sqm_year_current || "",
    kwh_sqm_year_target: existingProperty.kwh_sqm_year_target || "",
    heating_type: existingProperty.heating_type || "",
    cooling_type: existingProperty.cooling_type || "",

    land_register_id: existingProperty.land_register_id || "",
    ownership_type: existingProperty.ownership_type || "",

    photos: [],
  };
}

function normalizeCountryCode(value) {
  const text = String(value || "")
    .trim()
    .toUpperCase();

  if (["ES", "SPAIN", "ESPAÑA", "ESPANA"].includes(text)) return "ES";
  if (["BE", "BELGIUM", "BELGIQUE"].includes(text)) return "BE";

  return text;
}

function getLandRegisterLabel(country, t) {
  const normalized = normalizeCountryCode(country);

  if (normalized === "ES") {
    return t("fields.cadastralReference");
  }

  if (normalized === "BE") {
    return t("fields.cadastralParcelId");
  }

  return t("fields.landRegisterId");
}

function validateLandRegisterId(value, country, t) {
  const cleaned = String(value || "")
    .trim()
    .toUpperCase();
  const normalized = normalizeCountryCode(country);

  if (!cleaned) {
    return normalized === "ES" ? t("errors.required") : "";
  }

  if (normalized === "ES") {
    if (!/^[A-Z0-9]{20}$/.test(cleaned)) {
      return t("errors.invalidSpanishCadastre");
    }
  }

  if (normalized === "BE") {
    if (!/^[A-Z0-9\-\/.\s]+$/.test(cleaned)) {
      return t("errors.invalidBelgianCadastre");
    }
  }

  return "";
}

function toNumber(value) {
  const number = Number(String(value || "").replace(",", "."));
  return Number.isFinite(number) ? number : null;
}

function getRequiredImprovementPercent(marketRegionCode) {
  const code = String(marketRegionCode || "")
    .trim()
    .toUpperCase();

  if (code === "FLANDERS") return 60;
  if (code === "GIRONA") return 30;

  return null;
}

function calculateEnergyImprovement(currentValue, targetValue) {
  const current = toNumber(currentValue);
  const target = toNumber(targetValue);

  if (!current || current <= 0 || target === null || target < 0) {
    return null;
  }

  return ((current - target) / current) * 100;
}

const EPC_RANK = {
  A_PLUS: 1,
  A: 2,
  B: 3,
  C: 4,
  D: 5,
  E: 6,
  F: 7,
  G: 8,
  H: 9, // Legacy value only; H is not a selectable label in either pilot.
};

const FLANDERS_EPC_LABELS = ["A_PLUS", "A", "B", "C", "D", "E", "F"];
const GIRONA_EPC_LABELS = ["A", "B", "C", "D", "E", "F", "G"];
const DEFAULT_EPC_LABELS = ["A_PLUS", "A", "B", "C", "D", "E", "F", "G"];

const FLANDERS_EPC_LABEL_BY_KWH = [
  { label: "A_PLUS", max: 0 },
  { label: "A", max: 100 },
  { label: "B", max: 200 },
  { label: "C", max: 300 },
  { label: "D", max: 400 },
  { label: "E", max: 500 },
  { label: "F", max: Infinity },
];

function getExpectedFlandersEpcLabel(kwhValue) {
  const kwh = toNumber(kwhValue);
  if (kwh === null) return null;

  return (
    FLANDERS_EPC_LABEL_BY_KWH.find((band) => kwh <= band.max)?.label || null
  );
}

function isTargetEpcBetter(currentRating, targetRating) {
  if (!currentRating || !targetRating) return null;

  const currentRank = EPC_RANK[currentRating];
  const targetRank = EPC_RANK[targetRating];

  if (!currentRank || !targetRank) return null;

  return targetRank < currentRank;
}

function isTargetEpcConsistentWithFlandersKwh(targetRating, targetKwh) {
  const expectedLabel = getExpectedFlandersEpcLabel(targetKwh);

  if (!expectedLabel || !targetRating) return null;

  return EPC_RANK[targetRating] <= EPC_RANK[expectedLabel];
}

function estimateSpainRenovationCost(surfaceSqmValue) {
  const surfaceSqm = toNumber(surfaceSqmValue);

  if (!surfaceSqm || surfaceSqm <= 0) return null;

  const costPerSqm = 135;

  return {
    renovatedAreaSqm: surfaceSqm,
    costPerSqm,
    estimatedAllWorksCost: surfaceSqm * costPerSqm,
  };
}

function buildPropertyCalculations(form, marketRegionCode) {
  const regionCode = String(marketRegionCode || "")
    .trim()
    .toUpperCase();

  const requiredImprovementPercent = getRequiredImprovementPercent(regionCode);

  const energyImprovementPercent = calculateEnergyImprovement(
    form.kwh_sqm_year_current,
    form.kwh_sqm_year_target,
  );

  const gironaEstimate =
    regionCode === "GIRONA"
      ? estimateSpainRenovationCost(form.surface_sqm)
      : null;

  return {
    required_improvement_percent: requiredImprovementPercent,
    energy_improvement_percent: energyImprovementPercent,

    renovation_estimate_method: gironaEstimate ? "SPAIN_SURFACE_BASED" : "",

    estimated_renovation_cost_all_works:
      gironaEstimate?.estimatedAllWorksCost ?? null,

    suggested_renovation_budget: gironaEstimate?.estimatedAllWorksCost ?? null,

    renovated_area_sqm: gironaEstimate?.renovatedAreaSqm ?? null,

    cost_per_sqm: gironaEstimate?.costPerSqm ?? null,
  };
}

function getDefaultPropertyCountry(dossierDraft, profile) {
  return (
    normalizeCountryCode(
      dossierDraft?.market_region?.country_code ||
        dossierDraft?.dossier?.market_region?.country_code,
    ) ||
    normalizeCountryCode(profile?.country_of_property_purchase) ||
    normalizeCountryCode(profile?.country_of_residence) ||
    ""
  );
}

function buildPropertyStepFinancialInfo(
  currentFinancial = {},
  marketRegionCode,
  gironaEstimate,
  flandersEstimate,
) {
  const nextFinancial = { ...currentFinancial };
  const region = String(marketRegionCode || "").toUpperCase();
  const cost =
    region === "GIRONA"
      ? gironaEstimate?.estimatedAllWorksCost
      : region === "FLANDERS" && flandersEstimate?.available
        ? flandersEstimate?.estimated_cost_typical
        : null;

  if (cost === null || cost === undefined || cost === "") {
    return nextFinancial;
  }
  const numericCost = Number(cost);
  if (!Number.isFinite(numericCost) || numericCost < 0) {
    return nextFinancial;
  }

  const nextBudget = String(Math.round(numericCost));
  const previousBudget = String(
    currentFinancial.estimated_renovation_budget ?? "",
  );
  const currentLoan = String(currentFinancial.renovation_loan_amount ?? "");

  // Update requested amount only if it was blank or still equals the previous
  // automatic estimate. This preserves a genuinely user-chosen amount.
  if (
    !currentLoan ||
    currentLoan === previousBudget ||
    (currentLoan === "0" && (!previousBudget || previousBudget === "0"))
  ) {
    nextFinancial.renovation_loan_amount = nextBudget;
  }
  nextFinancial.estimated_renovation_budget = nextBudget;
  nextFinancial.renovation_estimate_source = "PROPERTY_INFORMATION";
  nextFinancial.renovation_estimate_method =
    region === "GIRONA" ? "SPAIN_SURFACE_BASED" : "FLANDERS_ABEX";
  if (region === "FLANDERS") {
    nextFinancial.epc_points_reduced = String(
      flandersEstimate?.epc_points_reduced ?? "",
    );
  }

  // Once the Property step changes the requested renovation amount, all
  // financing totals displayed in Review must reflect that same new amount.
  const propertyPrice = Number(nextFinancial.property_price);
  const equity = Number(nextFinancial.equity_amount);
  if (
    nextFinancial.property_price !== "" &&
    nextFinancial.property_price != null &&
    nextFinancial.equity_amount !== "" &&
    nextFinancial.equity_amount != null &&
    Number.isFinite(propertyPrice) &&
    Number.isFinite(equity)
  ) {
    const purchaseLoan = Math.max(propertyPrice - equity, 0);
    const renovationLoan = Number(nextFinancial.renovation_loan_amount || 0);
    const total = purchaseLoan + renovationLoan;
    nextFinancial.purchase_loan_amount = String(purchaseLoan);
    nextFinancial.total_loan_amount = String(total);
    const tenure = Number(nextFinancial.tenure_years);
    if (tenure > 0) {
      nextFinancial.estimated_monthly_payment = String(
        Math.round(total / (tenure * 12)),
      );
    }
  }
  return nextFinancial;
}

function formatEstimatedCost(value) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "—";
  }

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(number);
}

function formatEstimateReferenceDate(value) {
  if (!value) {
    return "";
  }

  const date = new Date(`${value}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

export default function HomebuyerPropertyInformation() {
  const { t } = useTranslation("homebuyer-property-info");
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const {
    currentStepKey,
    handleStepValidityChange,
    dossierDraft,
    updateDraftSection,
    updateDraftMetaFields,
    registerStepSubmitHandler,
    existingProperty,
    openSaveModal,
    profile,
    dossierId,
  } = useOutletContext();

  const activeDraft = dossierDraft || {};
  const activeDossierId = dossierId || activeDraft?.dossier_id || null;

  const dossierMarketRegion =
    activeDraft?.market_region ||
    activeDraft?.dossier?.market_region ||
    existingProperty?.market_region ||
    null;

  const dossierMarketRegionCode = String(dossierMarketRegion?.code || "")
    .trim()
    .toUpperCase();

  const isFlandersMarket = dossierMarketRegionCode === "FLANDERS";
  const isGironaMarket = dossierMarketRegionCode === "GIRONA";

  const availableEpcLabels = React.useMemo(() => {
    if (isFlandersMarket) return FLANDERS_EPC_LABELS;
    if (isGironaMarket) return GIRONA_EPC_LABELS;
    return DEFAULT_EPC_LABELS;
  }, [isFlandersMarket, isGironaMarket]);

  const dossierMarketCountry = normalizeCountryCode(
    dossierMarketRegion?.country_code ||
      activeDraft?.country_of_property_purchase ||
      profile?.country_of_property_purchase ||
      "",
  );

  const [form, setForm] = React.useState(() => {
    const draftProperty = dossierDraft?.property_information || {};
    const { formPatch } = hydrateOtherFields(draftProperty);

    return {
      ...DEFAULT_FORM,
      country: getDefaultPropertyCountry(dossierDraft, profile),
      ...draftProperty,
      ...formPatch,
    };
  });

  const [otherValues, setOtherValues] = React.useState(() => {
    const draftProperty = dossierDraft?.property_information || {};
    const { otherPatch } = hydrateOtherFields(draftProperty);

    return {
      building_condition: "",
      heating_type: "",
      cooling_type: "",
      property_type: "",
      ownership_type: "",
      ...otherPatch,
    };
  });

  const [fieldErrors, setFieldErrors] = React.useState({});
  const [mapsError, setMapsError] = React.useState("");
  const [selectedPreview, setSelectedPreview] = React.useState(null);

  const initialRenovationEstimate =
    activeDraft?.renovation_cost_estimate ||
    activeDraft?.dossier_financial_info?.renovation_cost_estimate ||
    null;

  const [flandersRenovationEstimate, setFlandersRenovationEstimate] =
    React.useState(
      initialRenovationEstimate
        ? { available: true, ...initialRenovationEstimate }
        : null,
    );

  const [isFlandersEstimateLoading, setIsFlandersEstimateLoading] =
    React.useState(false);

  const [flandersEstimateError, setFlandersEstimateError] = React.useState("");

  const requiredImprovementPercent = getRequiredImprovementPercent(
    dossierMarketRegionCode,
  );

  const energyImprovementPercent = calculateEnergyImprovement(
    form.kwh_sqm_year_current,
    form.kwh_sqm_year_target,
  );

  const meetsEnergyTarget =
    requiredImprovementPercent !== null &&
    energyImprovementPercent !== null &&
    energyImprovementPercent >= requiredImprovementPercent;

  const targetEpcBetter = isTargetEpcBetter(
    form.epc_rating_current,
    form.epc_rating_target,
  );

  const expectedFlandersTargetEpcLabel = isFlandersMarket
    ? getExpectedFlandersEpcLabel(form.kwh_sqm_year_target)
    : null;

  const targetEpcConsistentWithKwh = isFlandersMarket
    ? isTargetEpcConsistentWithFlandersKwh(
        form.epc_rating_target,
        form.kwh_sqm_year_target,
      )
    : null;

  const gironaRenovationEstimate = React.useMemo(() => {
    if (!isGironaMarket) return null;

    return estimateSpainRenovationCost(form.surface_sqm);
  }, [isGironaMarket, form.surface_sqm]);

  const hasFlandersEstimatorInputs =
    Boolean(form.property_type) &&
    Boolean(form.epc_rating_current) &&
    Boolean(form.epc_rating_target) &&
    Boolean(form.surface_sqm);

  React.useEffect(() => {
    if (!isFlandersMarket) {
      setFlandersRenovationEstimate(null);
      setFlandersEstimateError("");
      setIsFlandersEstimateLoading(false);
      return undefined;
    }

    if (!activeDossierId) {
      return undefined;
    }

    if (!hasFlandersEstimatorInputs) {
      setFlandersRenovationEstimate(null);
      setFlandersEstimateError("");
      setIsFlandersEstimateLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    const timeoutId = window.setTimeout(async () => {
      try {
        setIsFlandersEstimateLoading(true);
        setFlandersEstimateError("");

        const accessToken = await getAccessTokenSilently();

        const response = await fetch(
          `${API_BASE}/api/homeowner/dossiers/${activeDossierId}/renovation-cost-estimate/preview/`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              property_type: buildBackendPropertyPayload(form).property_type,
              current_epc_label: form.epc_rating_current,
              target_epc_label: form.epc_rating_target,
              floor_area_sqm: form.surface_sqm,
            }),
            signal: controller.signal,
          },
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data?.detail ||
              t(
                "renovationEstimate.previewFailed",
                "The renovation cost estimate could not be calculated.",
              ),
          );
        }

        setFlandersRenovationEstimate(data);
      } catch (error) {
        if (error?.name === "AbortError") {
          return;
        }

        console.error("Could not preview renovation estimate:", error);

        setFlandersRenovationEstimate(null);
        setFlandersEstimateError(
          error?.message ||
            t(
              "renovationEstimate.previewFailed",
              "The renovation cost estimate could not be calculated.",
            ),
        );
      } finally {
        if (!controller.signal.aborted) {
          setIsFlandersEstimateLoading(false);
        }
      }
    }, 350);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [
    activeDossierId,
    isFlandersMarket,
    hasFlandersEstimatorInputs,
    form.property_type,
    form.epc_rating_current,
    form.epc_rating_target,
    form.surface_sqm,
    getAccessTokenSilently,
    otherValues.property_type,
    t,
  ]);

  const hasRenovationEstimate = isFlandersMarket
    ? Boolean(flandersRenovationEstimate?.available)
    : Boolean(gironaRenovationEstimate);

  const calculatedPropertyInfo = React.useMemo(() => {
    return {
      ...form,
      ...buildPropertyCalculations(form, dossierMarketRegionCode),
    };
  }, [form, dossierMarketRegionCode]);

  const FIELD_TOOLTIPS = React.useMemo(
    () => ({
      property_type: t("tooltips.propertyType"),
      building_condition: t("tooltips.buildingCondition"),
      surface_sqm: t("tooltips.surfaceSqm"),
      number_of_rooms: t("tooltips.numberOfRooms"),
      year_of_build: t("tooltips.yearOfBuild"),
      has_lift: t("tooltips.hasLift"),
      comfort_type: t("tooltips.comfortType"),
      notes: t("tooltips.notes"),
      epc_rating_current: t("tooltips.epcRatingCurrent"),
      epc_rating_target: t("tooltips.epcRatingTarget"),
      kwh_sqm_year_current: t("tooltips.kwhCurrent"),
      kwh_sqm_year_target: t("tooltips.kwhTarget"),
      heating_type: t("tooltips.heatingType"),
      cooling_type: t("tooltips.coolingType"),
      land_register_id: t("tooltips.landRegisterId"),
      ownership_type: t("tooltips.ownershipType"),
      full_address: t("tooltips.searchAddress"),
      street_name: t("tooltips.streetName"),
      house_number: t("tooltips.houseNumber"),
      city: t("tooltips.city"),
      market_region: t(
        "tooltips.marketRegion",
        "Operational market region assigned to this dossier.",
      ),
      postal_code: t("tooltips.postalCode"),
      country: t("tooltips.country"),
      apartment_number: t("tooltips.apartmentNumber"),
      photos: t("tooltips.propertyPhotos"),
    }),
    [t],
  );

  const addressInputRef = React.useRef(null);
  const addressAutocompleteRef = React.useRef(null);
  const addressSearchTimerRef = React.useRef(null);
  const addressRequestIdRef = React.useRef(0);
  const addressSessionTokenRef = React.useRef(null);
  const placesLibraryPromiseRef = React.useRef(null);

  const mapRef = React.useRef(null);
  const googleMapRef = React.useRef(null);
  const markerRef = React.useRef(null);
  const prefilledRef = React.useRef(false);

  const [addressSuggestions, setAddressSuggestions] = React.useState([]);

  const [addressSuggestionsOpen, setAddressSuggestionsOpen] =
    React.useState(false);

  const [addressSearching, setAddressSearching] = React.useState(false);

  React.useEffect(() => {
    const defaultCountry = getDefaultPropertyCountry(dossierDraft, profile);

    if (!defaultCountry) return;

    setForm((prev) => {
      if (prev.country === defaultCountry) return prev;

      return {
        ...prev,
        country: defaultCountry,
      };
    });
  }, [dossierDraft, profile]);

  function buildBackendPropertyPayload(values) {
    return {
      ...values,
      building_condition:
        values.building_condition === "OTHER"
          ? otherValues.building_condition
          : values.building_condition,

      heating_type:
        values.heating_type === "OTHER"
          ? otherValues.heating_type
          : values.heating_type,

      cooling_type:
        values.cooling_type === "OTHER"
          ? otherValues.cooling_type
          : values.cooling_type,

      property_type:
        values.property_type === "OTHER"
          ? otherValues.property_type
          : values.property_type,

      ownership_type:
        values.ownership_type === "OTHER"
          ? otherValues.ownership_type
          : values.ownership_type,
    };
  }

  React.useEffect(() => {
    const backendPropertyInfo = buildBackendPropertyPayload(form);

    updateDraftSection("property_information", backendPropertyInfo);
  }, [form, updateDraftSection, otherValues]);

  React.useEffect(() => {
    if (prefilledRef.current) return;
    if (!existingProperty) return;
    if (dossierDraft?.property_information?.property_type) return;

    setForm((prev) => ({
      ...prev,
      ...mapExistingPropertyToForm(existingProperty),
    }));
    prefilledRef.current = true;
  }, [existingProperty, dossierDraft]);

  React.useEffect(() => {
    let mounted = true;

    async function initMap() {
      if (!GOOGLE_MAPS_API_KEY || !mapRef.current) {
        return;
      }

      try {
        const google = await loadGoogleMaps(GOOGLE_MAPS_API_KEY);

        if (!mounted || !google?.maps?.importLibrary || !mapRef.current) {
          return;
        }

        const [{ Map }, { AdvancedMarkerElement }] = await Promise.all([
          google.maps.importLibrary("maps"),
          google.maps.importLibrary("marker"),
        ]);

        if (!mounted || !mapRef.current) {
          return;
        }

        const center = {
          lat: Number(form.latitude) || 41.3851,
          lng: Number(form.longitude) || 2.1734,
        };

        googleMapRef.current = new Map(mapRef.current, {
          center,
          zoom: form.latitude && form.longitude ? 15 : 6,

          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          mapId: GOOGLE_MAPS_MAP_ID,
        });

        markerRef.current = new AdvancedMarkerElement({
          map: form.latitude && form.longitude ? googleMapRef.current : null,
          position: center,
        });

        setMapsError("");
      } catch (error) {
        console.error("Google Maps could not be loaded:", error);

        if (mounted) {
          setMapsError(t("messages.mapsLoadError"));
        }
      }
    }

    initMap();

    return () => {
      mounted = false;
    };
  }, [t]);

  async function getPlacesLibrary() {
    const google = await loadGoogleMaps(GOOGLE_MAPS_API_KEY);

    if (!google?.maps?.importLibrary) {
      return null;
    }

    if (!placesLibraryPromiseRef.current) {
      placesLibraryPromiseRef.current = google.maps.importLibrary("places");
    }

    return placesLibraryPromiseRef.current;
  }

  function handleAddressSearchChange(event) {
    const value = event.target.value;

    setForm((prev) => ({
      ...prev,
      full_address: value,
    }));

    setMapsError("");

    if (addressSearchTimerRef.current) {
      window.clearTimeout(addressSearchTimerRef.current);
    }

    const query = value.trim();

    if (query.length < 3 || !GOOGLE_MAPS_API_KEY) {
      setAddressSuggestions([]);
      setAddressSuggestionsOpen(false);
      setAddressSearching(false);
      return;
    }

    const requestId = ++addressRequestIdRef.current;

    setAddressSearching(true);
    setAddressSuggestionsOpen(true);

    addressSearchTimerRef.current = window.setTimeout(async () => {
      try {
        const placesLibrary = await getPlacesLibrary();

        if (!placesLibrary) {
          setAddressSuggestions([]);
          setAddressSuggestionsOpen(false);
          return;
        }

        const { AutocompleteSuggestion, AutocompleteSessionToken } =
          placesLibrary;

        if (!addressSessionTokenRef.current) {
          addressSessionTokenRef.current = new AutocompleteSessionToken();
        }

        const request = {
          input: query,

          sessionToken: addressSessionTokenRef.current,

          language: "en",
        };

        if (dossierMarketCountry) {
          request.includedRegionCodes = [dossierMarketCountry.toLowerCase()];

          request.region = dossierMarketCountry.toLowerCase();
        }

        const { suggestions } =
          await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        if (requestId !== addressRequestIdRef.current) {
          return;
        }

        const predictions = (suggestions || [])
          .map((suggestion) => suggestion.placePrediction)
          .filter(Boolean);

        setAddressSuggestions(predictions);

        setAddressSuggestionsOpen(predictions.length > 0);
      } catch (error) {
        console.error("Google address autocomplete failed:", error);

        if (requestId === addressRequestIdRef.current) {
          setAddressSuggestions([]);
          setAddressSuggestionsOpen(false);

          setMapsError(t("messages.mapsLoadError"));
        }
      } finally {
        if (requestId === addressRequestIdRef.current) {
          setAddressSearching(false);
        }
      }
    }, 300);
  }

  async function handleAddressSelect(prediction) {
    if (!prediction) {
      return;
    }

    try {
      setAddressSearching(true);

      const place = prediction.toPlace();

      await place.fetchFields({
        fields: ["id", "formattedAddress", "location", "addressComponents"],
      });

      if (!place.location) {
        return;
      }

      const latitude = place.location.lat();

      const longitude = place.location.lng();

      const selectedCountry = normalizeCountryCode(
        getPlaceAddressPart(place, "country", true),
      );

      if (
        dossierMarketCountry &&
        selectedCountry &&
        selectedCountry !== dossierMarketCountry
      ) {
        setMapsError(
          t(
            "messages.addressOutsideMarketCountry",
            "The selected address does not belong to the dossier market country.",
          ),
        );

        return;
      }

      const city =
        getPlaceAddressPart(place, "locality") ||
        getPlaceAddressPart(place, "postal_town") ||
        getPlaceAddressPart(place, "administrative_area_level_2");

      const next = {
        street_name: getPlaceAddressPart(place, "route"),

        house_number: getPlaceAddressPart(place, "street_number"),

        city,

        postal_code: getPlaceAddressPart(place, "postal_code"),

        country: selectedCountry,

        full_address: place.formattedAddress || prediction.text?.text || "",

        latitude,
        longitude,

        place_id: place.id || "",
      };

      setForm((prev) => ({
        ...prev,
        ...next,
      }));

      setAddressSuggestions([]);
      setAddressSuggestionsOpen(false);
      setMapsError("");

      addressSessionTokenRef.current = null;

      if (googleMapRef.current) {
        googleMapRef.current.setCenter({
          lat: latitude,
          lng: longitude,
        });

        googleMapRef.current.setZoom(16);
      }

      if (markerRef.current) {
        markerRef.current.position = {
          lat: latitude,
          lng: longitude,
        };

        markerRef.current.map = googleMapRef.current;
      }
    } catch (error) {
      console.error("Could not select Google address:", error);

      setMapsError(t("messages.mapsLoadError"));
    } finally {
      setAddressSearching(false);
    }
  }

  React.useEffect(() => {
    function handleDocumentClick(event) {
      if (
        addressAutocompleteRef.current &&
        !addressAutocompleteRef.current.contains(event.target)
      ) {
        setAddressSuggestionsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleDocumentClick);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);

      if (addressSearchTimerRef.current) {
        window.clearTimeout(addressSearchTimerRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (!googleMapRef.current || !markerRef.current) return;
    if (!form.latitude || !form.longitude) return;

    const position = {
      lat: Number(form.latitude),
      lng: Number(form.longitude),
    };

    googleMapRef.current.setCenter(position);
    markerRef.current.position = position;
    markerRef.current.map = googleMapRef.current;
  }, [form.latitude, form.longitude]);

  const photoUrlsRef = React.useRef([]);

  React.useEffect(() => {
    photoUrlsRef.current = (form.photos || [])
      .map((photo) => photo?.previewUrl)
      .filter((url) => typeof url === "string" && url.startsWith("blob:"));
  }, [form.photos]);

  React.useEffect(() => {
    return () => {
      photoUrlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, []);

  function handleChange(e) {
    const { name, value } = e.target;

    let nextValue = value;
    if (
      [
        "surface_sqm",
        "number_of_rooms",
        "year_of_build",
        "kwh_sqm_year_current",
        "kwh_sqm_year_target",
      ].includes(name)
    ) {
      nextValue = value.replace(/[^\d.]/g, "");
    }

    setForm((prev) => {
      const next = { ...prev, [name]: nextValue };

      if (
        [
          "street_name",
          "house_number",
          "city",
          "postal_code",
          "country",
          "apartment_number",
        ].includes(name)
      ) {
        next.full_address = buildFullAddress(next);
      }

      return next;
    });
  }

  function handlePhotoUpload(event) {
    const files = Array.from(event.target.files || []);
    const validFiles = files.filter((file) => file.type.startsWith("image/"));

    if (!validFiles.length) {
      event.target.value = "";
      return;
    }

    const nextPhotos = validFiles.map((file) => ({
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      file,
      name: file.name,
      previewUrl: URL.createObjectURL(file),
      mimeType: file.type,
    }));

    setForm((prev) => ({
      ...prev,
      photos: [...(prev.photos || []), ...nextPhotos],
    }));

    setSelectedPreview((prev) => prev || nextPhotos[0]?.previewUrl || null);

    event.target.value = "";
  }

  function handleRemovePhoto(photoId) {
    setForm((prev) => {
      const photoToRemove = (prev.photos || []).find((p) => p.id === photoId);
      if (photoToRemove?.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(photoToRemove.previewUrl);
      }

      const nextPhotos = (prev.photos || []).filter((p) => p.id !== photoId);

      if (selectedPreview === photoToRemove?.previewUrl) {
        setSelectedPreview(nextPhotos[0]?.previewUrl || null);
      }

      return {
        ...prev,
        photos: nextPhotos,
      };
    });
  }

  function validateForm(values) {
    const errors = {};

    if (!isFilled(values.property_type)) {
      errors.property_type = t("errors.required");
    }
    if (!isFilled(values.building_condition)) {
      errors.building_condition = t("errors.required");
    }
    if (!isFilled(values.surface_sqm)) {
      errors.surface_sqm = t("errors.required");
    }
    if (!isFilled(values.year_of_build)) {
      errors.year_of_build = t("errors.required");
    }

    if (!isFilled(values.street_name)) {
      errors.street_name = t("errors.required");
    }
    if (!isFilled(values.house_number)) {
      errors.house_number = t("errors.required");
    }
    if (!isFilled(values.city)) {
      errors.city = t("errors.required");
    }
    if (!isFilled(values.postal_code)) {
      errors.postal_code = t("errors.required");
    }
    if (!dossierMarketRegion?.id) {
      errors.market_region_id = t(
        "errors.marketRegionRequired",
        "A market region must be selected when the dossier is created.",
      );
    }

    if (!isFilled(values.country)) {
      errors.country = t("errors.required");
    } else if (
      dossierMarketCountry &&
      normalizeCountryCode(values.country) !== dossierMarketCountry
    ) {
      errors.country = t(
        "errors.countryMustMatchMarketRegion",
        "The property country must match the dossier market region.",
      );
    }

    if (!values.latitude || !values.longitude) {
      errors.full_address = t("errors.validAddress");
    }

    if (!isFilled(values.epc_rating_current)) {
      errors.epc_rating_current = t("errors.required");
    } else if (!availableEpcLabels.includes(values.epc_rating_current)) {
      errors.epc_rating_current = t("errors.invalidEpcForMarket", {
        defaultValue:
          "This EPC label is not valid for the selected market region.",
      });
    }

    if (isFlandersMarket && !isFilled(values.epc_rating_target)) {
      errors.epc_rating_target = t("errors.required");
    } else if (
      isFilled(values.epc_rating_target) &&
      !availableEpcLabels.includes(values.epc_rating_target)
    ) {
      errors.epc_rating_target = t("errors.invalidEpcForMarket", {
        defaultValue:
          "This EPC label is not valid for the selected market region.",
      });
    }
    if (!isFilled(values.kwh_sqm_year_current)) {
      errors.kwh_sqm_year_current = t("errors.required");
    }
    if (!isFilled(values.kwh_sqm_year_target)) {
      errors.kwh_sqm_year_target = t("errors.required");
    }
    const currentKwh = toNumber(values.kwh_sqm_year_current);
    const targetKwh = toNumber(values.kwh_sqm_year_target);

    if (currentKwh !== null && targetKwh !== null && targetKwh >= currentKwh) {
      errors.kwh_sqm_year_target = t("errors.targetMustBeLower");
    }
    if (
      isFilled(values.epc_rating_current) &&
      isFilled(values.epc_rating_target)
    ) {
      const epcImproves = isTargetEpcBetter(
        values.epc_rating_current,
        values.epc_rating_target,
      );

      if (epcImproves === false) {
        errors.epc_rating_target = t("errors.targetEpcMustImprove");
      }
    }
    if (!isFilled(values.heating_type)) {
      errors.heating_type = t("errors.required");
    }
    if (!isFilled(values.cooling_type)) {
      errors.cooling_type = t("errors.required");
    }

    errors.land_register_id = validateLandRegisterId(
      values.land_register_id,
      values.country,
      t,
    );

    if (!isFilled(values.ownership_type)) {
      errors.ownership_type = t("errors.required");
    }

    if (
      values.property_type === "OTHER" &&
      !isFilled(otherValues.property_type)
    ) {
      errors.property_type = t("errors.required");
    }

    if (
      values.building_condition === "OTHER" &&
      !isFilled(otherValues.building_condition)
    ) {
      errors.building_condition = t("errors.required");
    }

    if (
      values.heating_type === "OTHER" &&
      !isFilled(otherValues.heating_type)
    ) {
      errors.heating_type = t("errors.required");
    }

    if (
      values.cooling_type === "OTHER" &&
      !isFilled(otherValues.cooling_type)
    ) {
      errors.cooling_type = t("errors.required");
    }

    if (
      values.ownership_type === "OTHER" &&
      !isFilled(otherValues.ownership_type)
    ) {
      errors.ownership_type = t("errors.required");
    }

    return Object.fromEntries(
      Object.entries(errors).filter(([, value]) => Boolean(value)),
    );
  }

  React.useEffect(() => {
    const errors = validateForm(form);
    setFieldErrors(errors);

    handleStepValidityChange(
      currentStepKey,
      Object.keys(errors).length === 0,
      errors,
      form,
      calculatedPropertyInfo,
    );
  }, [
    form,
    currentStepKey,
    handleStepValidityChange,
    otherValues,
    dossierMarketRegion?.id,
    dossierMarketCountry,
    availableEpcLabels,
    t,
  ]);

  React.useEffect(() => {
    if (!hasRenovationEstimate) return;
    const current = dossierDraft?.dossier_financial_info || {};
    const derived = buildPropertyStepFinancialInfo(
      current,
      dossierMarketRegionCode,
      gironaRenovationEstimate,
      flandersRenovationEstimate,
    );
    const patch = {};
    for (const key of [
      "estimated_renovation_budget",
      "renovation_loan_amount",
      "renovation_estimate_source",
      "renovation_estimate_method",
      "epc_points_reduced",
      "purchase_loan_amount",
      "total_loan_amount",
      "estimated_monthly_payment",
    ]) {
      if (derived[key] !== undefined && derived[key] !== current[key]) {
        patch[key] = derived[key];
      }
    }
    if (Object.keys(patch).length) {
      updateDraftSection("dossier_financial_info", patch);
    }
  }, [
    hasRenovationEstimate,
    dossierDraft?.dossier_financial_info,
    dossierMarketRegionCode,
    gironaRenovationEstimate,
    flandersRenovationEstimate,
    updateDraftSection,
  ]);

  React.useEffect(() => {
    async function handlePropertyStepSubmit() {
      if (!activeDossierId || !dossierMarketRegion?.id) {
        return { ok: false };
      }

      try {
        const accessToken = await getAccessTokenSilently();

        const backendPropertyInfo = buildBackendPropertyPayload(form);

        const nextFinancialInfo = buildPropertyStepFinancialInfo(
          dossierDraft?.dossier_financial_info || {},
          dossierMarketRegionCode,
          gironaRenovationEstimate,
          flandersRenovationEstimate,
        );

        const result = await updateDossierPropertyInfo({
          accessToken,
          dossierId: activeDossierId,
          propertyInfo: backendPropertyInfo,
          dossierFinancialInfo: nextFinancialInfo,
        });

        const savedRenovationEstimate =
          result.renovation_cost_estimate ||
          result.dossier_financial_info?.renovation_cost_estimate ||
          null;

        if (isFlandersMarket && savedRenovationEstimate) {
          setFlandersRenovationEstimate({
            available: true,
            ...savedRenovationEstimate,
          });
        }

        updateDraftMetaFields({
          dossier_id: result.dossier_id || activeDossierId,

          dossier_code: result.dossier_code || activeDraft.dossier_code || null,

          property_information:
            result.property_information || backendPropertyInfo,

          dossier_financial_info:
            result.dossier_financial_info || nextFinancialInfo,

          renovation_cost_estimate: savedRenovationEstimate,
        });

        return {
          ok: true,
          updatedDraft: {
            ...activeDraft,

            property_information:
              result.property_information || backendPropertyInfo,

            dossier_financial_info:
              result.dossier_financial_info || nextFinancialInfo,

            renovation_cost_estimate: savedRenovationEstimate,
          },
        };
      } catch (err) {
        console.error("Could not save property information:", err);
        return {
          ok: false,
          error: err.message || "Could not save property information.",
        };
      }
    }

    registerStepSubmitHandler(currentStepKey, handlePropertyStepSubmit);

    return () => {
      registerStepSubmitHandler(currentStepKey, null);
    };
  }, [
    activeDossierId,
    activeDraft,
    currentStepKey,
    dossierDraft,
    form,
    dossierMarketRegion?.id,
    dossierMarketRegionCode,
    gironaRenovationEstimate,
    flandersRenovationEstimate,
    getAccessTokenSilently,
    registerStepSubmitHandler,
    updateDraftMetaFields,
  ]);

  function renderLabel(label, fieldName) {
    return (
      <label className="hb-field-label-row">
        <span>
          {label}
          {isRequiredField(fieldName, form.country, isFlandersMarket) ? (
            <span className="hb-required-asterisk"> *</span>
          ) : null}
        </span>

        <span className="hb-tooltip-wrap" tabIndex={0}>
          <Info size={14} strokeWidth={2.2} />
          <span className="hb-tooltip">{FIELD_TOOLTIPS[fieldName]}</span>
        </span>
      </label>
    );
  }

  function renderError(fieldName) {
    const message = fieldErrors[fieldName];
    if (!message || message === t("errors.required")) return null;
    return (
      <span
        id={`hb-property-error-${fieldName}`}
        className="hb-field-error hb-property-validation-error"
        role="alert"
      >
        {message}
      </span>
    );
  }

  function renderInputWithSuffix(name, value, placeholder, suffix) {
    return (
      <div className="hb-input-with-suffix">
        <input
          name={name}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          aria-invalid={Boolean(fieldErrors[name])}
          aria-describedby={
            fieldErrors[name] ? `hb-property-error-${name}` : undefined
          }
        />
        <span className="hb-input-with-suffix__suffix">{suffix}</span>
      </div>
    );
  }

  return (
    <div className="hb-property-page">
      <div className="hb-step-page__header">
        <h1 className="hb-step-page__title">{t("title")}</h1>

        <button
          type="button"
          className="hb-app-flow__save-btn"
          onClick={openSaveModal}
        >
          <Save size={16} />
          <span>{t("buttons.save")}</span>
        </button>
      </div>

      <div className="hb-property-layout">
        <div className="hb-property-column">
          <section className="hb-property-card">
            <h2 className="hb-property-card__title">
              {t("sections.propertyInformation")}
            </h2>

            <div className="hb-property-grid">
              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.propertyType"), "property_type")}
                <select
                  name="property_type"
                  aria-invalid={Boolean(fieldErrors.property_type)}
                  aria-describedby={
                    fieldErrors.property_type
                      ? "hb-property-error-property_type"
                      : undefined
                  }
                  value={form.property_type}
                  onChange={handleChange}
                >
                  <option value="">{t("options.select")}</option>
                  <option value="APARTMENT_FLAT">
                    {t("options.propertyType.apartmentFlat")}
                  </option>
                  <option value="STUDIO_APARTMENT">
                    {t("options.propertyType.studioApartment")}
                  </option>
                  <option value="SINGLE_FAMILY_HOUSE_DETACHED">
                    {t("options.propertyType.singleFamilyHouseDetached")}
                  </option>
                  <option value="SEMI_DETACHED_HOUSE">
                    {t("options.propertyType.semiDetachedHouse")}
                  </option>
                  <option value="TERRACED_ROW_HOUSE">
                    {t("options.propertyType.terracedRowHouse")}
                  </option>
                  <option value="DUPLEX_MAISONETTE">
                    {t("options.propertyType.duplexMaisonette")}
                  </option>
                  <option value="TOWNHOUSE">
                    {t("options.propertyType.townhouse")}
                  </option>
                  <option value="MULTI_FAMILY_RESIDENTIAL_BUILDING">
                    {t("options.propertyType.multiFamilyResidentialBuilding")}
                  </option>
                  <option value="OTHER">
                    {t("options.propertyType.other")}
                  </option>
                </select>
                {form.property_type === "OTHER" && (
                  <div className="hb-field hb-field--with-tooltip">
                    <input
                      value={otherValues.property_type}
                      onChange={(e) =>
                        setOtherValues((prev) => ({
                          ...prev,
                          property_type: e.target.value,
                        }))
                      }
                      placeholder={t("placeholders.otherSpecify")}
                    />
                  </div>
                )}
                {renderError("property_type")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(
                  t("fields.buildingCondition"),
                  "building_condition",
                )}
                <select
                  name="building_condition"
                  aria-invalid={Boolean(fieldErrors.building_condition)}
                  aria-describedby={
                    fieldErrors.building_condition
                      ? "hb-property-error-building_condition"
                      : undefined
                  }
                  value={form.building_condition}
                  onChange={handleChange}
                >
                  <option value="">{t("options.select")}</option>
                  <option value="EXCELLENT">
                    {t("options.buildingCondition.excellent")}
                  </option>
                  <option value="VERY_GOOD">
                    {t("options.buildingCondition.veryGood")}
                  </option>
                  <option value="GOOD">
                    {t("options.buildingCondition.good")}
                  </option>
                  <option value="FAIR_AVERAGE">
                    {t("options.buildingCondition.fairAverage")}
                  </option>
                  <option value="POOR">
                    {t("options.buildingCondition.poor")}
                  </option>
                  <option value="DILAPIDATED">
                    {t("options.buildingCondition.dilapidated")}
                  </option>
                  <option value="UNKNOWN">
                    {t("options.buildingCondition.unknown")}
                  </option>
                  <option value="OTHER">
                    {t("options.buildingCondition.other")}
                  </option>
                </select>
                {form.building_condition === "OTHER" && (
                  <input
                    value={otherValues.building_condition}
                    onChange={(e) =>
                      setOtherValues((prev) => ({
                        ...prev,
                        building_condition: e.target.value,
                      }))
                    }
                    placeholder={t("placeholders.otherSpecify")}
                  />
                )}
                {renderError("building_condition")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.surfaceSqm"), "surface_sqm")}
                {renderInputWithSuffix(
                  "surface_sqm",
                  form.surface_sqm,
                  t("placeholders.surfaceSqm"),
                  t("units.squareMeters"),
                )}
                {renderError("surface_sqm")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.numberOfRooms"), "number_of_rooms")}
                <input
                  name="number_of_rooms"
                  value={form.number_of_rooms}
                  onChange={handleChange}
                  placeholder={t("placeholders.numberOfRooms")}
                />
                {renderError("number_of_rooms")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.yearOfBuild"), "year_of_build")}
                <select
                  name="year_of_build"
                  aria-invalid={Boolean(fieldErrors.year_of_build)}
                  aria-describedby={
                    fieldErrors.year_of_build
                      ? "hb-property-error-year_of_build"
                      : undefined
                  }
                  value={form.year_of_build}
                  onChange={handleChange}
                >
                  <option value="">{t("options.select")}</option>
                  {Array.from(
                    { length: new Date().getFullYear() - 1799 },
                    (_, i) => {
                      const year = String(new Date().getFullYear() - i);
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    },
                  )}
                </select>
                {renderError("year_of_build")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.hasLift"), "has_lift")}
                <select
                  name="has_lift"
                  value={form.has_lift}
                  onChange={handleChange}
                >
                  <option value="">{t("options.select")}</option>
                  <option value="YES">{t("options.yes")}</option>
                  <option value="NO">{t("options.no")}</option>
                </select>
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.comfortType"), "comfort_type")}
                <select
                  name="comfort_type"
                  value={form.comfort_type}
                  onChange={handleChange}
                >
                  <option value="">{t("options.select")}</option>
                  <option value="BASIC">
                    {t("options.comfortType.basic")}
                  </option>
                  <option value="STANDARD">
                    {t("options.comfortType.standard")}
                  </option>
                  <option value="GOOD">{t("options.comfortType.good")}</option>
                  <option value="HIGH">{t("options.comfortType.high")}</option>
                  <option value="VERY_HIGH_PREMIUM">
                    {t("options.comfortType.veryHighPremium")}
                  </option>
                </select>
              </div>

              <div className="hb-field hb-field--with-tooltip hb-field--full">
                {renderLabel(t("fields.notes"), "notes")}
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  rows={3}
                  placeholder={t("placeholders.notes")}
                />
              </div>
            </div>
          </section>

          <section className="hb-property-card">
            <h2 className="hb-property-card__title">
              {t("sections.energeticDetails")}
            </h2>

            <div className="hb-property-grid">
              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(
                  t("fields.epcRatingCurrent"),
                  "epc_rating_current",
                )}
                <select
                  name="epc_rating_current"
                  aria-invalid={Boolean(fieldErrors.epc_rating_current)}
                  aria-describedby={
                    fieldErrors.epc_rating_current
                      ? "hb-property-error-epc_rating_current"
                      : undefined
                  }
                  value={form.epc_rating_current}
                  onChange={handleChange}
                >
                  <option value="">{t("options.select")}</option>
                  {availableEpcLabels.map((label) => (
                    <option key={label} value={label}>
                      {label === "A_PLUS" ? "A+" : label}
                    </option>
                  ))}
                </select>
                {renderError("epc_rating_current")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.epcRatingTarget"), "epc_rating_target")}
                <select
                  name="epc_rating_target"
                  aria-invalid={Boolean(fieldErrors.epc_rating_target)}
                  aria-describedby={
                    fieldErrors.epc_rating_target
                      ? "hb-property-error-epc_rating_target"
                      : undefined
                  }
                  value={form.epc_rating_target}
                  onChange={handleChange}
                >
                  <option value="">{t("options.select")}</option>
                  {availableEpcLabels.map((label) => (
                    <option key={label} value={label}>
                      {label === "A_PLUS" ? "A+" : label}
                    </option>
                  ))}
                </select>
                {renderError("epc_rating_target")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.kwhCurrent"), "kwh_sqm_year_current")}
                {renderInputWithSuffix(
                  "kwh_sqm_year_current",
                  form.kwh_sqm_year_current,
                  t("placeholders.kwhCurrent"),
                  t("units.kwhPerSqmYear"),
                )}
                {renderError("kwh_sqm_year_current")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.kwhTarget"), "kwh_sqm_year_target")}
                {renderInputWithSuffix(
                  "kwh_sqm_year_target",
                  form.kwh_sqm_year_target,
                  t("placeholders.kwhTarget"),
                  t("units.kwhPerSqmYear"),
                )}
                {renderError("kwh_sqm_year_target")}
              </div>
              {requiredImprovementPercent !== null && (
                <div className="hb-energy-target-box">
                  <div className="hb-energy-target-box__title">
                    {t("energyTarget.title")}
                  </div>

                  <p>
                    {isFlandersMarket
                      ? t("energyTarget.flandersDescription")
                      : t("energyTarget.spainDescription")}
                  </p>

                  {energyImprovementPercent !== null && (
                    <div
                      className={`hb-energy-target-box__result ${
                        meetsEnergyTarget ? "is-ok" : "is-warning"
                      }`}
                    >
                      <strong>
                        {t("energyTarget.currentImprovement", {
                          value: energyImprovementPercent.toFixed(1),
                        })}
                      </strong>

                      <span>
                        {meetsEnergyTarget
                          ? t("energyTarget.targetMet", {
                              value: requiredImprovementPercent,
                            })
                          : t("energyTarget.targetNotMet", {
                              value: requiredImprovementPercent,
                            })}
                      </span>
                    </div>
                  )}
                  {isFlandersMarket && (
                    <div className="hb-renovation-estimate">
                      <div className="hb-renovation-estimate__header">
                        <div>
                          <strong>
                            {t(
                              "renovationEstimate.title",
                              "Indicative renovation cost",
                            )}
                          </strong>

                          <span>
                            {t(
                              "renovationEstimate.description",
                              "Estimated energy-renovation budget based on the C-REAL Flanders reference projects.",
                            )}
                          </span>
                        </div>

                        {flandersRenovationEstimate?.abex_value ? (
                          <span className="hb-renovation-estimate__abex">
                            {t("renovationEstimate.abex", "ABEX {{value}}", {
                              value: flandersRenovationEstimate.abex_value,
                            })}
                          </span>
                        ) : null}
                      </div>

                      {!hasFlandersEstimatorInputs ? (
                        <div className="hb-renovation-estimate__status">
                          {t(
                            "renovationEstimate.completeInputs",
                            "Enter the property type, floor area, current EPC label and target EPC label to calculate the indicative renovation cost.",
                          )}
                        </div>
                      ) : null}

                      {hasFlandersEstimatorInputs && !activeDossierId ? (
                        <div className="hb-renovation-estimate__warning">
                          {t(
                            "renovationEstimate.saveDossierFirst",
                            "The dossier must be created before the renovation estimate can be calculated.",
                          )}
                        </div>
                      ) : null}

                      {isFlandersEstimateLoading ? (
                        <div className="hb-renovation-estimate__status">
                          {t(
                            "renovationEstimate.calculating",
                            "Calculating estimate...",
                          )}
                        </div>
                      ) : null}

                      {!isFlandersEstimateLoading && flandersEstimateError ? (
                        <div className="hb-renovation-estimate__warning">
                          {flandersEstimateError}
                        </div>
                      ) : null}

                      {!isFlandersEstimateLoading &&
                      !flandersEstimateError &&
                      flandersRenovationEstimate &&
                      flandersRenovationEstimate.available === false ? (
                        <div className="hb-renovation-estimate__warning">
                          {flandersRenovationEstimate.reason ===
                          "TARGET_EPC_MUST_BE_BETTER"
                            ? t(
                                "renovationEstimate.targetMustImprove",
                                "Select a target EPC label that is better than the current label.",
                              )
                            : flandersRenovationEstimate.reason ===
                                "CURRENT_EPC_NOT_SUPPORTED"
                              ? t(
                                  "renovationEstimate.currentLabelUnsupported",
                                  "The estimator currently supports starting EPC labels B to F.",
                                )
                              : flandersRenovationEstimate.reason ===
                                  "REFERENCE_VERSION_NOT_CONFIGURED"
                                ? t(
                                    "renovationEstimate.notConfigured",
                                    "The renovation estimator reference data has not been configured yet.",
                                  )
                                : t(
                                    "renovationEstimate.unavailable",
                                    "The renovation estimate is not available for the current inputs.",
                                  )}
                        </div>
                      ) : null}

                      {!isFlandersEstimateLoading &&
                      !flandersEstimateError &&
                      flandersRenovationEstimate?.available ? (
                        <>
                          <div className="hb-renovation-estimate__values">
                            <div className="hb-renovation-estimate__value">
                              <span>
                                {t("renovationEstimate.low", "Low estimate")}
                              </span>

                              <strong>
                                {formatEstimatedCost(
                                  flandersRenovationEstimate.estimated_cost_low,
                                )}
                              </strong>
                            </div>

                            <div className="hb-renovation-estimate__value is-primary">
                              <span>
                                {t(
                                  "renovationEstimate.typical",
                                  "Typical estimate",
                                )}
                              </span>

                              <strong>
                                {formatEstimatedCost(
                                  flandersRenovationEstimate.estimated_cost_typical,
                                )}
                              </strong>
                            </div>

                            <div className="hb-renovation-estimate__value">
                              <span>
                                {t("renovationEstimate.high", "High estimate")}
                              </span>

                              <strong>
                                {formatEstimatedCost(
                                  flandersRenovationEstimate.estimated_cost_high,
                                )}
                              </strong>
                            </div>
                          </div>

                          <div className="hb-renovation-estimate__meta">
                            <span>
                              {t(
                                "renovationEstimate.reference",
                                "Reference: {{current}} → {{target}}, {{area}} m²",
                                {
                                  current:
                                    flandersRenovationEstimate.current_epc_label,
                                  target:
                                    flandersRenovationEstimate.target_epc_label,
                                  area: flandersRenovationEstimate.floor_area_sqm,
                                },
                              )}
                            </span>

                            {flandersRenovationEstimate.abex_effective_date ? (
                              <span>
                                {t(
                                  "renovationEstimate.priceReference",
                                  "Prices adjusted with ABEX effective {{date}}.",
                                  {
                                    date: formatEstimateReferenceDate(
                                      flandersRenovationEstimate.abex_effective_date,
                                    ),
                                  },
                                )}
                              </span>
                            ) : null}
                          </div>

                          {flandersRenovationEstimate.warnings?.includes(
                            "LOW_REFERENCE_SAMPLE",
                          ) ? (
                            <div className="hb-renovation-estimate__warning">
                              {t(
                                "renovationEstimate.lowReferenceSample",
                                "This starting EPC label has a smaller direct reference sample, so nearby EPC groups are also used.",
                              )}
                            </div>
                          ) : null}

                          {flandersRenovationEstimate.warnings?.includes(
                            "FLOOR_AREA_OUTSIDE_REFERENCE_RANGE",
                          ) ? (
                            <div className="hb-renovation-estimate__warning">
                              {t(
                                "renovationEstimate.areaOutsideRange",
                                "The floor area is outside the 107–415 m² range of the reference projects, so the estimate is less representative.",
                              )}
                            </div>
                          ) : null}

                          {flandersRenovationEstimate.warnings?.includes(
                            "PROPERTY_TYPE_OUTSIDE_REFERENCE_SAMPLE",
                          ) ? (
                            <div className="hb-renovation-estimate__warning">
                              {t(
                                "renovationEstimate.propertyTypeOutsideReference",
                                "The estimate is available for this property type, but the C-REAL reference sample mainly contains houses. The result should therefore be treated with additional caution.",
                              )}
                            </div>
                          ) : null}

                          <p className="hb-renovation-estimate__note">
                            {t(
                              "renovationEstimate.disclaimer",
                              "Indicative estimate only. It covers energy-related renovation measures and is not a contractor quote or a requested loan amount.",
                            )}
                          </p>

                          <div className="hb-renovation-estimate__actions">
                            <button
                              type="button"
                              className="hb-renovation-estimate__financial-button"
                              onClick={() => navigate("../financial-info")}
                            >
                              {t(
                                "renovationEstimate.goToFinancialInfo",
                                "Go to Financial Information",
                              )}
                            </button>
                          </div>
                        </>
                      ) : null}
                    </div>
                  )}
                  {isGironaMarket && hasRenovationEstimate ? (
                    <div className="hb-renovation-estimate">
                      <div className="hb-renovation-estimate__header">
                        <div>
                          <strong>
                            {t(
                              "renovationEstimate.title",
                              "Indicative renovation cost",
                            )}
                          </strong>

                          <span>
                            {t(
                              "renovationEstimate.gironaDescription",
                              "Estimated renovation budget based on the property floor area and the Girona reference cost.",
                            )}
                          </span>
                        </div>
                      </div>

                      <div className="hb-renovation-estimate__values">
                        <div className="hb-renovation-estimate__value is-primary">
                          <span>
                            {t(
                              "renovationEstimate.estimatedBudget",
                              "Estimated renovation budget",
                            )}
                          </span>

                          <strong>
                            {formatEstimatedCost(
                              gironaRenovationEstimate.estimatedAllWorksCost,
                            )}
                          </strong>
                        </div>
                      </div>

                      <div className="hb-renovation-estimate__meta">
                        <span>
                          {t(
                            "renovationEstimate.gironaReference",
                            "{{area}} m² × {{cost}}/m²",
                            {
                              area: gironaRenovationEstimate.renovatedAreaSqm,
                              cost: formatEstimatedCost(
                                gironaRenovationEstimate.costPerSqm,
                              ),
                            },
                          )}
                        </span>
                      </div>

                      <p className="hb-renovation-estimate__note">
                        {t(
                          "renovationEstimate.gironaFinancialInfoNote",
                          "This estimated budget will be carried to the Financial Information step, where you can review it together with the financing amounts.",
                        )}
                      </p>

                      <div className="hb-renovation-estimate__actions">
                        <button
                          type="button"
                          className="hb-renovation-estimate__financial-button"
                          onClick={() => navigate("../financial-info")}
                        >
                          {t(
                            "renovationEstimate.goToFinancialInfo",
                            "Go to Financial Information",
                          )}
                        </button>
                      </div>
                    </div>
                  ) : null}
                  {targetEpcBetter === false && (
                    <div className="hb-energy-target-box__warning">
                      {t("energyTarget.epcNotImproved")}
                    </div>
                  )}

                  {isFlandersMarket && targetEpcConsistentWithKwh === false && (
                    <div className="hb-energy-target-box__warning">
                      {t("energyTarget.flandersEpcWarning", {
                        expectedLabel: expectedFlandersTargetEpcLabel,
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.heatingType"), "heating_type")}
                <select
                  name="heating_type"
                  aria-invalid={Boolean(fieldErrors.heating_type)}
                  aria-describedby={
                    fieldErrors.heating_type
                      ? "hb-property-error-heating_type"
                      : undefined
                  }
                  value={form.heating_type}
                  onChange={handleChange}
                >
                  <option value="">{t("options.select")}</option>
                  <option value="NONE">{t("options.heatingType.none")}</option>
                  <option value="GAS_BOILER">
                    {t("options.heatingType.gasBoiler")}
                  </option>
                  <option value="AIR_CONDITION">
                    {t("options.heatingType.airCondition")}
                  </option>
                  <option value="HEAT_PUMP">
                    {t("options.heatingType.heatPump")}
                  </option>
                  <option value="DISTRICT_HEATING">
                    {t("options.heatingType.districtHeating")}
                  </option>
                  <option value="OTHER">
                    {t("options.heatingType.other")}
                  </option>
                  <option value="UNKNOWN">
                    {t("options.heatingType.unknown")}
                  </option>
                </select>
                {form.heating_type === "OTHER" && (
                  <div className="hb-field hb-field--with-tooltip">
                    <input
                      value={otherValues.heating_type}
                      onChange={(e) =>
                        setOtherValues((prev) => ({
                          ...prev,
                          heating_type: e.target.value,
                        }))
                      }
                      placeholder={t("placeholders.otherSpecify")}
                    />
                  </div>
                )}
                {renderError("heating_type")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.coolingType"), "cooling_type")}
                <select
                  name="cooling_type"
                  aria-invalid={Boolean(fieldErrors.cooling_type)}
                  aria-describedby={
                    fieldErrors.cooling_type
                      ? "hb-property-error-cooling_type"
                      : undefined
                  }
                  value={form.cooling_type}
                  onChange={handleChange}
                >
                  <option value="">{t("options.select")}</option>
                  <option value="NONE">{t("options.coolingType.none")}</option>
                  <option value="AIR_CONDITION">
                    {t("options.coolingType.airCondition")}
                  </option>
                  <option value="OTHER">
                    {t("options.coolingType.other")}
                  </option>
                  <option value="UNKNOWN">
                    {t("options.coolingType.unknown")}
                  </option>
                </select>
                {form.cooling_type === "OTHER" && (
                  <div className="hb-field hb-field--with-tooltip">
                    <input
                      value={otherValues.cooling_type}
                      onChange={(e) =>
                        setOtherValues((prev) => ({
                          ...prev,
                          cooling_type: e.target.value,
                        }))
                      }
                      placeholder={t("placeholders.otherSpecify")}
                    />
                  </div>
                )}
                {renderError("cooling_type")}
              </div>
            </div>
          </section>

          <section className="hb-property-card">
            <h2 className="hb-property-card__title">
              {t("sections.registrationCadaster")}
            </h2>

            <div className="hb-property-grid">
              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(
                  getLandRegisterLabel(form.country, t),
                  "land_register_id",
                )}
                <input
                  name="land_register_id"
                  aria-invalid={Boolean(fieldErrors.land_register_id)}
                  aria-describedby={
                    fieldErrors.land_register_id
                      ? "hb-property-error-land_register_id"
                      : undefined
                  }
                  value={form.land_register_id}
                  onChange={handleChange}
                  placeholder={
                    normalizeCountryCode(form.country) === "ES"
                      ? t("placeholders.cadastralReference")
                      : normalizeCountryCode(form.country) === "BE"
                        ? t("placeholders.cadastralParcelId")
                        : t("placeholders.landRegisterId")
                  }
                />
                {renderError("land_register_id")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.ownershipType"), "ownership_type")}
                <select
                  name="ownership_type"
                  aria-invalid={Boolean(fieldErrors.ownership_type)}
                  aria-describedby={
                    fieldErrors.ownership_type
                      ? "hb-property-error-ownership_type"
                      : undefined
                  }
                  value={form.ownership_type}
                  onChange={handleChange}
                >
                  <option value="">{t("options.select")}</option>
                  <option value="SOLE_OWNER">
                    {t("options.ownershipType.soleOwner")}
                  </option>
                  <option value="CO_OWNER_SPOUSE_PARTNER">
                    {t("options.ownershipType.coOwnerSpousePartner")}
                  </option>
                  <option value="CO_OWNER_OTHER_PERSONS">
                    {t("options.ownershipType.coOwnerOtherPersons")}
                  </option>
                  <option value="USUFRUCT_RIGHT_OF_USE">
                    {t("options.ownershipType.usufructRightOfUse")}
                  </option>
                  <option value="OTHER">
                    {t("options.ownershipType.other")}
                  </option>
                </select>
                {form.ownership_type === "OTHER" && (
                  <div className="hb-field hb-field--with-tooltip">
                    <input
                      value={otherValues.ownership_type}
                      onChange={(e) =>
                        setOtherValues((prev) => ({
                          ...prev,
                          ownership_type: e.target.value,
                        }))
                      }
                      placeholder={t("placeholders.otherSpecify")}
                    />
                  </div>
                )}
                {renderError("ownership_type")}
              </div>
            </div>
          </section>
        </div>

        <div className="hb-property-column">
          <section className="hb-property-card">
            <h2 className="hb-property-card__title">{t("sections.address")}</h2>

            <div className="hb-field hb-field--with-tooltip hb-field--full">
              {renderLabel(t("fields.searchAddress"), "full_address")}
              <div
                className="hb-property-address-autocomplete"
                ref={addressAutocompleteRef}
              >
                <input
                  ref={addressInputRef}
                  name="full_address"
                  aria-invalid={Boolean(fieldErrors.full_address)}
                  aria-describedby={
                    fieldErrors.full_address
                      ? "hb-property-error-full_address"
                      : undefined
                  }
                  value={form.full_address}
                  onChange={handleAddressSearchChange}
                  onFocus={() => {
                    if (addressSuggestions.length > 0) {
                      setAddressSuggestionsOpen(true);
                    }
                  }}
                  placeholder={t("placeholders.searchAddress")}
                  autoComplete="off"
                />

                {addressSearching ? (
                  <span className="hb-property-address-autocomplete__loading">
                    {t("messages.searchingAddress", "Searching...")}
                  </span>
                ) : null}

                {addressSuggestionsOpen && addressSuggestions.length > 0 ? (
                  <div className="hb-property-address-autocomplete__suggestions">
                    {addressSuggestions.map((prediction, index) => (
                      <button
                        key={
                          prediction.placeId || prediction.text?.text || index
                        }
                        type="button"
                        className="hb-property-address-autocomplete__suggestion"
                        onMouseDown={(event) => {
                          event.preventDefault();
                        }}
                        onClick={() => handleAddressSelect(prediction)}
                      >
                        {prediction.text?.text ||
                          prediction.mainText?.text ||
                          ""}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              {renderError("full_address")}
              {!GOOGLE_MAPS_API_KEY && (
                <span className="hb-field-hint">
                  {t("messages.mapsKeyMissing")}
                </span>
              )}
              {mapsError && <span className="hb-field-error">{mapsError}</span>}
            </div>

            <div className="hb-property-map-wrap">
              {GOOGLE_MAPS_API_KEY ? (
                <div ref={mapRef} className="hb-property-map" />
              ) : (
                <div className="hb-property-map hb-property-map--placeholder">
                  <MapPin size={20} />
                  <span>{t("messages.mapPreviewPlaceholder")}</span>
                </div>
              )}
            </div>

            <div className="hb-property-grid">
              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.streetName"), "street_name")}
                <input
                  name="street_name"
                  aria-invalid={Boolean(fieldErrors.street_name)}
                  aria-describedby={
                    fieldErrors.street_name
                      ? "hb-property-error-street_name"
                      : undefined
                  }
                  value={form.street_name}
                  onChange={handleChange}
                  placeholder={t("placeholders.streetName")}
                />
                {renderError("street_name")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.houseNumber"), "house_number")}
                <input
                  name="house_number"
                  aria-invalid={Boolean(fieldErrors.house_number)}
                  aria-describedby={
                    fieldErrors.house_number
                      ? "hb-property-error-house_number"
                      : undefined
                  }
                  value={form.house_number}
                  onChange={handleChange}
                  placeholder={t("placeholders.houseNumber")}
                />
                {renderError("house_number")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.city"), "city")}
                <input
                  name="city"
                  aria-invalid={Boolean(fieldErrors.city)}
                  aria-describedby={
                    fieldErrors.city ? "hb-property-error-city" : undefined
                  }
                  value={form.city}
                  onChange={handleChange}
                  placeholder={t("placeholders.city")}
                />
                {renderError("city")}
              </div>
              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(
                  t("fields.region", "Market region"),
                  "market_region",
                )}

                <input
                  value={
                    dossierMarketRegion?.name ||
                    t("messages.marketRegionMissingValue", "Not selected")
                  }
                  readOnly
                  disabled
                  className={
                    fieldErrors.market_region_id ? "hb-input-error" : ""
                  }
                />

                {fieldErrors.market_region_id ? (
                  <span className="hb-field-error">
                    {fieldErrors.market_region_id}
                  </span>
                ) : (
                  <span className="hb-field-hint">
                    {t(
                      "messages.marketRegionFromDossier",
                      "The market region is selected when the dossier is created and is shared with lenders and OSS partners.",
                    )}
                  </span>
                )}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.postalCode"), "postal_code")}
                <input
                  name="postal_code"
                  aria-invalid={Boolean(fieldErrors.postal_code)}
                  aria-describedby={
                    fieldErrors.postal_code
                      ? "hb-property-error-postal_code"
                      : undefined
                  }
                  value={form.postal_code}
                  onChange={handleChange}
                  placeholder={t("placeholders.postalCode")}
                />
                {renderError("postal_code")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.country"), "country")}
                <select
                  name="country"
                  aria-invalid={Boolean(fieldErrors.country)}
                  aria-describedby={
                    fieldErrors.country
                      ? "hb-property-error-country"
                      : undefined
                  }
                  value={form.country}
                  onChange={handleChange}
                  disabled={Boolean(dossierMarketRegion?.id)}
                >
                  <option value="">{t("options.select")}</option>

                  {PROPERTY_PURCHASE_COUNTRIES.map((country) => (
                    <option key={country.code} value={country.code}>
                      {t(`common:countries.${country.code}`, {
                        defaultValue: country.label,
                      })}
                    </option>
                  ))}
                </select>
                {renderError("country")}

                {dossierMarketRegion?.id && (
                  <span className="hb-field-hint">
                    {t(
                      "messages.countryFromMarketRegion",
                      "The property country is determined by the dossier market region.",
                    )}
                  </span>
                )}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.apartmentNumber"), "apartment_number")}
                <input
                  name="apartment_number"
                  value={form.apartment_number}
                  onChange={handleChange}
                  placeholder={t("placeholders.apartmentNumber")}
                />
              </div>
            </div>
          </section>

          <section className="hb-property-card">
            <h2 className="hb-property-card__title">
              {t("sections.propertyPhotos")}
            </h2>

            <label className="hb-photo-upload">
              <input
                type="file"
                hidden
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
              />
              <div className="hb-photo-upload__inner">
                <Upload size={18} />
                <span>{t("messages.photoUpload")}</span>
              </div>
            </label>

            {form.photos?.length > 0 ? (
              <>
                <div className="hb-photo-grid">
                  {form.photos.map((photo) => (
                    <div
                      key={photo.id}
                      className={`hb-photo-thumb ${
                        selectedPreview === photo.previewUrl ? "is-active" : ""
                      }`}
                      onClick={() => setSelectedPreview(photo.previewUrl)}
                    >
                      <img
                        src={photo.previewUrl}
                        alt={photo.name}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                      <button
                        type="button"
                        className="hb-photo-thumb__remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemovePhoto(photo.id);
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="hb-photo-preview">
                  {selectedPreview ? (
                    <img
                      src={selectedPreview}
                      alt={t("messages.selectedPreviewAlt")}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : null}
                </div>
              </>
            ) : (
              <div className="hb-photo-empty">
                <ImageIcon size={18} />
                <span>{t("messages.noPhotos")}</span>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
